import { mapValues } from 'lodash';
import memoizeOne from 'memoize-one';
import { useCallback, useRef } from 'react';
import { useIsomorphicLayoutEffect } from 'react-use';

import { useStableCallback } from '../../utils/hooks';
// @ts-ignore
import { showNetworkError } from '../notification/actions';

import { RobotProps, RobotRunCode } from './robot.types';
import { getCodeFromUserAnswer } from './utils/getCodeFromUserAnswer';

// Вызываем метод воспроизведения кода или берем
// готовые результаты из попытки или авторского решения
export function useRunCode(code: string, props: RobotProps): RobotRunCode {
    const { postRunCode, problem, attempt, dispatch } = props;

    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    const memoRef = useRef<typeof runCode>();

    const runCode = useCallback(
        async (answer: string) => {
            if (!postRunCode) {
                throw new Error('postRunCode is not provided');
            }

            try {
                const response = await postRunCode({
                    problemId: problem.id,
                    markers: { user_answer: answer },
                });

                return response.attempt.markers;
            } catch (e) {
                memoRef.current = undefined;
                console.error(e);
                dispatch?.(showNetworkError(e));
                throw e;
            }
        },
        [dispatch, postRunCode, problem.id]
    );

    useIsomorphicLayoutEffect(() => {
        memoRef.current = undefined;
    }, [runCode]);

    // Стабильный референс к функции воспроизведения текущего кода
    return useStableCallback(async () => {
        if (attempt && code === getCodeFromUserAnswer(attempt.markers.user_answer)) {
            return attempt.markers;
        }

        if (code === problem.markup.author_solution) {
            return {
                user_answer: code,
                result: mapValues(problem.markup.author_solution_run, (sample) => ({
                    answer_status: true,
                    check_details: { sample },
                    mistake_count: 0,
                })),
            };
        }

        memoRef.current = memoRef.current || memoizeOne(runCode);

        return memoRef.current(code);
    });
}
